import React, { useState, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  useTheme,
  alpha,
  LinearProgress,
  Divider,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Sanitizer as BottleIcon,
  CameraRoll as LabelIcon,
  Opacity as RawMaterialIcon,
  AllInbox as SupplyIcon,
  FiberManualRecord as CapsIcon,
  ShoppingBag as ProductIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as CostIcon,
  ShoppingCart as OrderIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const InventoryData = ({ companyId }) => {
  const theme = useTheme();
  const [inventoryData, setInventoryData] = useState({
    summary: {
      totalValue: 0,
      totalItems: 0,
      lowStockItems: 0,
      categories: {
        bottles: { count: 0, value: 0 },
        caps: { count: 0, value: 0 },
        labels: { count: 0, value: 0 },
        products: { count: 0, value: 0 },
        rawMaterials: { count: 0, value: 0 },
        supplies: { count: 0, value: 0 }
      }
    },
    mostExpensive: [],
    mostOrdered: [],
    recentTransactions: [],
    expiringItems: [],
    costAnalysis: {
      averageCost: 0,
      highestCost: 0,
      lowestCost: 0
    }
  });

  // Pagination state for transactions
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  
  // Expiry days filter
  const [expiryDays, setExpiryDays] = useState(30);

  const fetchInventoryData = useCallback(async () => {
    try {
      // Fetch bottles inventory
      const { data: bottlesData } = await supabase
        .from('bottle_inventory')
        .select(`
          current_quantity,
          bottles!inner(
            name,
            unit_cost,
            supplier_id,
            suppliers(name)
          )
        `)
        .eq('company_id', companyId);

      // Fetch caps inventory
      const { data: capsData } = await supabase
        .from('caps_inventory')
        .select(`
          current_quantity,
          caps!inner(
            name,
            unit_cost,
            supplier_id,
            suppliers(name)
          )
        `)
        .eq('company_id', companyId);

      // Fetch labels inventory
      const { data: labelsData } = await supabase
        .from('label_inventory')
        .select(`
          current_quantity,
          labels!inner(
            name,
            unit_cost,
            supplier_id,
            suppliers(name)
          )
        `)
        .eq('company_id', companyId);

      // Fetch products inventory with variants for single_unit_cost
      const { data: productsData } = await supabase
        .from('product_inventory')
        .select(`
          total_units,
          product_id,
          variant_id,
          products!inner(
            name,
            brand_id,
            brands(name)
          ),
          product_variants(
            single_unit_cost
          )
        `)
        .eq('company_id', companyId);

      // Fetch raw materials inventory
      const { data: rawMaterialsData } = await supabase
        .from('raw_materials_inventory')
        .select(`
          current_unit_quantity,
          current_total_quantity,
          raw_materials!inner(
            name,
            unit_cost,
            supplier_id,
            suppliers(name)
          )
        `)
        .eq('company_id', companyId);

      // Fetch supplies inventory
      const { data: suppliesData } = await supabase
        .from('supply_inventory')
        .select(`
          current_quantity,
          supplies!inner(
            name,
            unit_cost,
            supplier_id,
            suppliers(name)
          )
        `)
        .eq('company_id', companyId);

      const { data: bottleTransactions } = await supabase
        .from('bottle_transactions')
        .select('*')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      const { data: capsTransactions } = await supabase
        .from('caps_transactions')
        .select('*')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      const { data: labelsTransactions } = await supabase
        .from('label_transactions')
        .select('*')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      const { data: suppliesTransactions } = await supabase
        .from('supply_transactions')
        .select('*')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      const { data: rawMaterialTransactions } = await supabase
        .from('raw_materials_transactions')
        .select(`
          *,
          raw_materials!inner(
            primary_unit_type,
            primary_unit_measure
          )
        `)
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      // Get item names separately
      const { data: allBottles } = await supabase
        .from('bottles')
        .select('id, name')
        .eq('company_id', companyId);

      const { data: allCaps } = await supabase
        .from('caps')
        .select('id, name')
        .eq('company_id', companyId);

      const { data: allLabels } = await supabase
        .from('labels')
        .select('id, name')
        .eq('company_id', companyId);

      const { data: allSupplies } = await supabase
        .from('supplies')
        .select('id, name')
        .eq('company_id', companyId);

      const { data: allRawMaterials } = await supabase
        .from('raw_materials')
        .select('id, name')
        .eq('company_id', companyId);

      const { data: productTransactions } = await supabase
        .from('product_inventory_transactions')
        .select(`
          id,
          transaction_type,
          quantity_change,
          unit_cost,
          created_at,
          products!inner(name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Fetch expiring raw materials (items expiring within selected days or already expired)
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + expiryDays);
      const formattedDate = futureDate.toISOString().split('T')[0];

      const { data: expiringRawMaterials } = await supabase
        .from('raw_materials_inventory_items')
        .select(`
          id,
          lot_number,
          batch_number,
          expiry_date,
          current_unit_quantity,
          current_total_quantity,
          raw_materials!inner(
            name,
            primary_unit_type,
            primary_unit_measure
          )
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .not('expiry_date', 'is', null)
        .lte('expiry_date', formattedDate)
        .gt('current_unit_quantity', 0)
        .order('expiry_date', { ascending: true });

      // Fetch expiring products (items expiring within 30 days or already expired)
      const { data: expiringProducts } = await supabase
        .from('product_inventory_items')
        .select(`
          id,
          lot_number,
          batch_number,
          expiry_date,
          unit_quantity,
          case_quantity,
          pallet_quantity,
          total_base_units,
          products!inner(
            name,
            brands(name)
          )
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .not('expiry_date', 'is', null)
        .lte('expiry_date', formattedDate)
        .gt('total_base_units', 0)
        .order('expiry_date', { ascending: true });

      // Process data
      const processedData = processInventoryData({
        bottles: bottlesData || [],
        caps: capsData || [],
        labels: labelsData || [],
        products: productsData || [],
        rawMaterials: rawMaterialsData || [],
        supplies: suppliesData || [],
        transactions: {
          bottles: bottleTransactions || [],
          caps: capsTransactions || [],
          labels: labelsTransactions || [],
          supplies: suppliesTransactions || [],
          rawMaterials: rawMaterialTransactions || [],
          products: productTransactions || []
        },
        itemNames: {
          bottles: allBottles || [],
          caps: allCaps || [],
          labels: allLabels || [],
          supplies: allSupplies || [],
          rawMaterials: allRawMaterials || []
        },
        expiring: {
          rawMaterials: expiringRawMaterials || [],
          products: expiringProducts || []
        }
      });

      setInventoryData(processedData);

    } catch (err) {
      console.error('Error fetching inventory data:', err);
      // Initialize empty data on error
      setInventoryData({
        summary: {
          totalValue: 0,
          totalItems: 0,
          lowStockItems: 0,
          categories: {
            bottles: { count: 0, value: 0 },
            caps: { count: 0, value: 0 },
            labels: { count: 0, value: 0 },
            products: { count: 0, value: 0 },
            rawMaterials: { count: 0, value: 0 },
            supplies: { count: 0, value: 0 }
          }
        },
        mostExpensive: [],
        mostOrdered: [],
        recentTransactions: [],
        expiringItems: [],
        costAnalysis: {
          averageCost: 0,
          highestCost: 0,
          lowestCost: 0
        }
      });
    }
  }, [companyId, expiryDays]);

  React.useEffect(() => {
    if (companyId) {
      fetchInventoryData();
    }
  }, [companyId, fetchInventoryData]);

  const processInventoryData = (data) => {
    const allItems = [];
    const categories = {
      bottles: { count: 0, value: 0 },
      caps: { count: 0, value: 0 },
      labels: { count: 0, value: 0 },
      products: { count: 0, value: 0 },
      rawMaterials: { count: 0, value: 0 },
      supplies: { count: 0, value: 0 }
    };

    // Process bottles
    data.bottles.forEach(item => {
      const value = (item.current_quantity || 0) * (item.bottles.unit_cost || 0);
      categories.bottles.count += item.current_quantity || 0;
      categories.bottles.value += value;
      allItems.push({
        name: item.bottles.name,
        type: 'Bottles',
        quantity: item.current_quantity || 0,
        unitCost: item.bottles.unit_cost || 0,
        totalValue: value,
        supplier: item.bottles.suppliers?.name || 'Unknown'
      });
    });

    // Process caps
    data.caps.forEach(item => {
      const value = (item.current_quantity || 0) * (item.caps.unit_cost || 0);
      categories.caps.count += item.current_quantity || 0;
      categories.caps.value += value;
      allItems.push({
        name: item.caps.name,
        type: 'Caps',
        quantity: item.current_quantity || 0,
        unitCost: item.caps.unit_cost || 0,
        totalValue: value,
        supplier: item.caps.suppliers?.name || 'Unknown'
      });
    });

    // Process labels
    data.labels.forEach(item => {
      const value = (item.current_quantity || 0) * (item.labels.unit_cost || 0);
      categories.labels.count += item.current_quantity || 0;
      categories.labels.value += value;
      allItems.push({
        name: item.labels.name,
        type: 'Labels',
        quantity: item.current_quantity || 0,
        unitCost: item.labels.unit_cost || 0,
        totalValue: value,
        supplier: item.labels.suppliers?.name || 'Unknown'
      });
    });

    // Process products (use single_unit_cost from product_variants like ProductInventoryOverview)
    data.products.forEach(item => {
      const actualUnitCost = item.product_variants?.single_unit_cost || 0;
      const value = (item.total_units || 0) * actualUnitCost;
      categories.products.count += item.total_units || 0;
      categories.products.value += value;
      allItems.push({
        name: item.products.name,
        type: 'Products',
        quantity: item.total_units || 0,
        unitCost: actualUnitCost,
        totalValue: value,
        supplier: item.products.brands?.name || 'Unknown Brand'
      });
    });

    // Process raw materials
    data.rawMaterials.forEach(item => {
      const value = (item.current_total_quantity || 0) * (item.raw_materials.unit_cost || 0);
      categories.rawMaterials.count += item.current_unit_quantity || 0;
      categories.rawMaterials.value += value;
      allItems.push({
        name: item.raw_materials.name,
        type: 'Raw Materials',
        quantity: item.current_unit_quantity || 0,
        unitCost: item.raw_materials.unit_cost || 0,
        totalValue: value,
        supplier: item.raw_materials.suppliers?.name || 'Unknown'
      });
    });

    // Process supplies
    data.supplies.forEach(item => {
      const value = (item.current_quantity || 0) * (item.supplies.unit_cost || 0);
      categories.supplies.count += item.current_quantity || 0;
      categories.supplies.value += value;
      allItems.push({
        name: item.supplies.name,
        type: 'Supplies',
        quantity: item.current_quantity || 0,
        unitCost: item.supplies.unit_cost || 0,
        totalValue: value,
        supplier: item.supplies.suppliers?.name || 'Unknown'
      });
    });

    // Calculate totals
    const totalValue = Object.values(categories).reduce((sum, cat) => sum + cat.value, 0);
    const totalItems = Object.values(categories).reduce((sum, cat) => sum + cat.count, 0);
    
    // Get most expensive items
    const mostExpensive = allItems
      .filter(item => item.unitCost > 0)
      .sort((a, b) => b.unitCost - a.unitCost)
      .slice(0, 5);

    // Get items with highest quantities (most ordered/stocked)
    const mostOrdered = allItems
      .filter(item => item.quantity > 0)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    // Calculate cost analysis
    const costsArray = allItems.filter(item => item.unitCost > 0).map(item => item.unitCost);
    const averageCost = costsArray.length > 0 ? costsArray.reduce((sum, cost) => sum + cost, 0) / costsArray.length : 0;
    const highestCost = costsArray.length > 0 ? Math.max(...costsArray) : 0;
    const lowestCost = costsArray.length > 0 ? Math.min(...costsArray) : 0;

    const bottleNameMap = {};
    const capNameMap = {};
    const labelNameMap = {};
    const supplyNameMap = {};
    const rawMaterialNameMap = {};

    // Build lookup maps
    if (data.itemNames) {
      data.itemNames.bottles?.forEach(item => {
        bottleNameMap[item.id] = item.name;
      });
      data.itemNames.caps?.forEach(item => {
        capNameMap[item.id] = item.name;
      });
      data.itemNames.labels?.forEach(item => {
        labelNameMap[item.id] = item.name;
      });
      data.itemNames.supplies?.forEach(item => {
        supplyNameMap[item.id] = item.name;
      });
      data.itemNames.rawMaterials?.forEach(item => {
        rawMaterialNameMap[item.id] = item.name;
      });
    }

    // Process recent transactions with lookup
    const recentTransactions = [];
    
    data.transactions.bottles.forEach(tx => {
      const itemName = bottleNameMap[tx.bottle_id] || `Bottle ${tx.bottle_id?.substring(0, 8) || 'Unknown'}`;
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: itemName,
        category: 'Bottles',
        quantity: tx.quantity,
        cost: tx.unit_cost,
        date: tx.transaction_date
      });
    });

    data.transactions.caps.forEach(tx => {
      const itemName = capNameMap[tx.cap_id] || `Cap ${tx.cap_id?.substring(0, 8) || 'Unknown'}`;
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: itemName,
        category: 'Caps',
        quantity: tx.quantity,
        cost: tx.unit_cost,
        date: tx.transaction_date
      });
    });

    data.transactions.labels.forEach(tx => {
      const itemName = labelNameMap[tx.label_id] || `Label ${tx.label_id?.substring(0, 8) || 'Unknown'}`;
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: itemName,
        category: 'Labels',
        quantity: tx.quantity,
        cost: tx.unit_cost,
        date: tx.transaction_date
      });
    });

    data.transactions.supplies.forEach(tx => {
      const itemName = supplyNameMap[tx.supply_id] || `Supply ${tx.supply_id?.substring(0, 8) || 'Unknown'}`;
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: itemName,
        category: 'Supplies',
        quantity: tx.quantity,
        cost: tx.unit_cost,
        date: tx.transaction_date
      });
    });

    data.transactions.rawMaterials.forEach(tx => {
      const itemName = rawMaterialNameMap[tx.raw_material_id] || `Raw Material ${tx.raw_material_id?.substring(0, 8) || 'Unknown'}`;
      // Calculate total transaction cost for purchases
      const totalTransactionCost = tx.transaction_type === 'purchase' 
        ? ((Math.abs(tx.total_quantity || 0) * (tx.unit_cost || 0)) + (tx.tax_amount || 0) + (tx.shipping_cost || 0))
        : tx.unit_cost;
      
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: itemName,
        category: 'Raw Materials',
        quantity: tx.unit_quantity,
        totalQuantity: tx.total_quantity,
        unitType: tx.raw_materials?.primary_unit_type || 'units',
        unitMeasure: tx.raw_materials?.primary_unit_measure || '',
        cost: totalTransactionCost,
        date: tx.transaction_date
      });
    });

    // Add product transactions
    data.transactions.products.forEach(tx => {
      recentTransactions.push({
        id: tx.id,
        type: tx.transaction_type,
        item: tx.products.name,
        category: 'Products',
        quantity: tx.quantity_change,
        cost: tx.unit_cost,
        date: tx.created_at
      });
    });

    // Sort by date 
    recentTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Process expiring items
    const expiringItems = [];
    const today = new Date();
    
    // Process expiring raw materials
    if (data.expiring?.rawMaterials) {
      data.expiring.rawMaterials.forEach(item => {
        const expiryDate = new Date(item.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        const isExpired = daysUntilExpiry < 0;
        
        expiringItems.push({
          id: item.id,
          name: item.raw_materials.name,
          type: 'Raw Materials',
          lotNumber: item.lot_number || 'N/A',
          batchNumber: item.batch_number || 'N/A',
          expiryDate: item.expiry_date,
          daysUntilExpiry,
          isExpired,
          quantity: item.current_unit_quantity,
          totalQuantity: item.current_total_quantity,
          unitType: item.raw_materials.primary_unit_type || 'units',
          unit: item.raw_materials.primary_unit_measure || ''
        });
      });
    }
    
    // Process expiring products
    if (data.expiring?.products) {
      data.expiring.products.forEach(item => {
        const expiryDate = new Date(item.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        const isExpired = daysUntilExpiry < 0;
        
        expiringItems.push({
          id: item.id,
          name: item.products.name,
          type: 'Products',
          brand: item.products.brands?.name || 'Unknown Brand',
          lotNumber: item.lot_number || 'N/A',
          batchNumber: item.batch_number || 'N/A',
          expiryDate: item.expiry_date,
          daysUntilExpiry,
          isExpired,
          quantity: item.total_base_units,
          unit: 'units'
        });
      });
    }
    
    // Sort by expiry date (most urgent first)
    expiringItems.sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));

    return {
      summary: {
        totalValue,
        totalItems,
        lowStockItems: allItems.filter(item => item.quantity < 10).length,
        categories
      },
      mostExpensive,
      mostOrdered,
      recentTransactions: recentTransactions, // SHOW ALL TRANSACTIONS
      expiringItems,
      costAnalysis: {
        averageCost,
        highestCost,
        lowestCost
      }
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'bottles': return <BottleIcon sx={{ fontSize: 20 }} />;
      case 'caps': return <CapsIcon sx={{ fontSize: 20 }} />;
      case 'labels': return <LabelIcon sx={{ fontSize: 20 }} />;
      case 'products': return <ProductIcon sx={{ fontSize: 20 }} />;
      case 'raw materials': return <RawMaterialIcon sx={{ fontSize: 20 }} />;
      case 'supplies': return <SupplyIcon sx={{ fontSize: 20 }} />;
      default: return <InventoryIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getCategoryColor = (type) => {
    const colors = {
      'bottles': '#2196F3',
      'caps': '#FF9800',
      'labels': '#4CAF50',
      'products': '#9C27B0',
      'raw materials': '#F44336',
      'supplies': '#607D8B'
    };
    return colors[type.toLowerCase()] || '#666';
  };

  // Pagination handlers
  const handleTransactionPageChange = (event, newPage) => {
    setTransactionPage(newPage);
  };

  const handleTransactionRowsPerPageChange = (event) => {
    setTransactionRowsPerPage(parseInt(event.target.value, 10));
    setTransactionPage(0);
  };

  return (
    <Box sx={{ mb: 6 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  mr: 2
                }}>
                  <CostIcon sx={{ fontSize: 24, color: 'success.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Inventory Value
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'success.main',
                mb: 1
              }}>
                {formatCurrency(inventoryData.summary.totalValue)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Across all categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  mr: 2
                }}>
                  <InventoryIcon sx={{ fontSize: 24, color: 'info.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Items
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'info.main',
                mb: 1
              }}>
                {formatNumber(inventoryData.summary.totalItems)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Units in stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  mr: 2
                }}>
                  <TrendingDownIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Low Stock Items
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'warning.main',
                mb: 1
              }}>
                {formatNumber(inventoryData.summary.lowStockItems)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Need reordering
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mr: 2
                }}>
                  <CostIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Cost
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main',
                mb: 1
              }}>
                {formatCurrency(inventoryData.costAnalysis.averageCost)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Per unit
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Category Breakdown */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Inventory by Category
              </Typography>
              <Grid container spacing={3}>
                {Object.entries(inventoryData.summary.categories).map(([key, data]) => {
                  const categoryName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                  const color = getCategoryColor(categoryName);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        bgcolor: alpha(color, 0.05),
                        border: `1px solid ${alpha(color, 0.2)}`,
                        cursor: 'default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(color, 0.08),
                          border: `1px solid ${alpha(color, 0.3)}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(color, 0.15)}`
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1, 
                            bgcolor: alpha(color, 0.1),
                            mr: 2,
                            color: color
                          }}>
                            {getCategoryIcon(categoryName)}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {categoryName}
                          </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 500, 
                          color: color,
                          mb: 1
                        }}>
                          {formatNumber(data.count)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {formatCurrency(data.value)} total value
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(data.value / inventoryData.summary.totalValue) * 100} 
                          sx={{ 
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(color, 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: color,
                              borderRadius: 3
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expiring Items Alert Section */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    mr: 2
                  }}>
                    <WarningIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 500, 
                      color: 'text.primary',
                      mb: 0.5
                    }}>
                      Expiring Items Alert
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Items expiring within {expiryDays} days or already expired
                    </Typography>
                  </Box>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    sx={{ 
                      bgcolor: 'white',
                      fontWeight: 500,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.grey[400], 0.8)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.warning.main
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.warning.main
                      }
                    }}
                  >
                    <MenuItem value={7}>Next 7 days</MenuItem>
                    <MenuItem value={14}>Next 14 days</MenuItem>
                    <MenuItem value={30}>Next 30 days</MenuItem>
                    <MenuItem value={60}>Next 60 days</MenuItem>
                    <MenuItem value={90}>Next 90 days</MenuItem>
                    <MenuItem value={120}>Next 120 days</MenuItem>
                    <MenuItem value={180}>Next 180 days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {inventoryData.expiringItems.length > 0 ? (
                <TableContainer sx={{ 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.5)}, white)`,
                  overflow: 'hidden'
                }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Item Name
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Type
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Lot / Batch
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Quantity
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Expiry Date
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.warning.main}`,
                          color: theme.palette.warning.main,
                          py: 1.5,
                          px: 2.5
                        }}>
                          Days Until Expiry
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryData.expiringItems.map((item, index) => (
                        <TableRow key={index} sx={{
                          cursor: 'default',
                          transition: 'all 0.2s ease',
                          bgcolor: item.isExpired ? alpha(theme.palette.error.main, 0.03) : 'transparent',
                          '&:hover': { 
                            bgcolor: item.isExpired ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.warning.main, 0.05),
                            transform: 'translateX(4px)',
                            boxShadow: `inset 4px 0 0 ${item.isExpired ? theme.palette.error.main : theme.palette.warning.main}`
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            px: 2.5
                          }
                        }}>
                          <TableCell>
                            {item.isExpired ? (
                              <Chip 
                                icon={<ErrorIcon />}
                                label="EXPIRED"
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  color: 'error.main',
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                }}
                              />
                            ) : item.daysUntilExpiry <= 7 ? (
                              <Chip 
                                icon={<ErrorIcon />}
                                label="URGENT"
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  color: 'error.main',
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                }}
                              />
                            ) : (
                              <Chip 
                                icon={<WarningIcon />}
                                label="WARNING"
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                                  color: 'warning.main',
                                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500,
                              color: 'text.primary'
                            }}>
                              {item.name}
                            </Typography>
                            {item.brand && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {item.brand}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ 
                                p: 0.5, 
                                borderRadius: 1, 
                                bgcolor: alpha(getCategoryColor(item.type), 0.1),
                                mr: 1,
                                color: getCategoryColor(item.type)
                              }}>
                                {getCategoryIcon(item.type)}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.type}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.lotNumber}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Batch: {item.batchNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {item.type === 'Raw Materials' && item.unitType && item.unit ? (
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatNumber(item.quantity)} {item.unitType}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  ({formatNumber(item.totalQuantity)} {item.unit})
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatNumber(item.quantity)} {item.unit}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500,
                              color: item.isExpired ? 'error.main' : 'text.primary'
                            }}>
                              {formatDate(item.expiryDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500,
                              color: item.isExpired 
                                ? 'error.main' 
                                : item.daysUntilExpiry <= 7 
                                  ? 'error.main' 
                                  : 'warning.main'
                            }}>
                              {item.isExpired 
                                ? `Expired ${Math.abs(item.daysUntilExpiry)} days ago` 
                                : `${item.daysUntilExpiry} days`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    mb: 2
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: 'success.main',
                    mb: 1
                  }}>
                    All Clear!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No items expiring within the next {expiryDays} days
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Tables */}
      <Grid container spacing={4}>
        {/* Most Expensive Items by Category */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Most Expensive Items by Type (Unit Cost)
              </Typography>
              {inventoryData.mostExpensive.length > 0 ? (
                <Box>
                  {/* Group by category */}
                  {['Bottles', 'Caps', 'Labels', 'Products', 'Raw Materials', 'Supplies'].map(category => {
                    const categoryItems = inventoryData.mostExpensive.filter(item => item.type === category).slice(0, 2);
                    if (categoryItems.length === 0) return null;
                    
                    return (
                      <Box key={category} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ 
                            p: 0.5, 
                            borderRadius: 1, 
                            bgcolor: alpha(getCategoryColor(category), 0.1),
                            mr: 1,
                            color: getCategoryColor(category)
                          }}>
                            {getCategoryIcon(category)}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: getCategoryColor(category) }}>
                            {category}
                          </Typography>
                        </Box>
                        {categoryItems.map((item, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: alpha(getCategoryColor(category), 0.05),
                            border: `1px solid ${alpha(getCategoryColor(category), 0.1)}`,
                            cursor: 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(getCategoryColor(category), 0.08),
                              border: `1px solid ${alpha(getCategoryColor(category), 0.2)}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(getCategoryColor(category), 0.12)}`
                            }
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {item.supplier}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                              {formatCurrency(item.unitCost)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No cost data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Highest Stock Items by Category */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Highest Stock Items by Type
              </Typography>
              {inventoryData.mostOrdered.length > 0 ? (
                <Box>
                  {/* Group by category */}
                  {['Bottles', 'Caps', 'Labels', 'Products', 'Raw Materials', 'Supplies'].map(category => {
                    const categoryItems = inventoryData.mostOrdered.filter(item => item.type === category).slice(0, 2);
                    if (categoryItems.length === 0) return null;
                    
                    return (
                      <Box key={category} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ 
                            p: 0.5, 
                            borderRadius: 1, 
                            bgcolor: alpha(getCategoryColor(category), 0.1),
                            mr: 1,
                            color: getCategoryColor(category)
                          }}>
                            {getCategoryIcon(category)}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: getCategoryColor(category) }}>
                            {category}
                          </Typography>
                        </Box>
                        {categoryItems.map((item, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: alpha(getCategoryColor(category), 0.05),
                            border: `1px solid ${alpha(getCategoryColor(category), 0.1)}`,
                            cursor: 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(getCategoryColor(category), 0.08),
                              border: `1px solid ${alpha(getCategoryColor(category), 0.2)}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(getCategoryColor(category), 0.12)}`
                            }
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {item.supplier}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main' }}>
                              {formatNumber(item.quantity)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No inventory data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Transactions */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Recent Transactions by Type
              </Typography>
              {inventoryData.recentTransactions.length > 0 ? (
                <Box>
                  {/* Group by category with table format and pagination */}
                  {['Bottles', 'Caps', 'Labels', 'Products', 'Raw Materials', 'Supplies'].map(category => {
                    const categoryTransactions = inventoryData.recentTransactions.filter(tx => tx.category === category);
                    if (categoryTransactions.length === 0) return null;
                    
                    const startIndex = transactionPage * transactionRowsPerPage;
                    const endIndex = startIndex + transactionRowsPerPage;
                    const paginatedTransactions = categoryTransactions.slice(startIndex, endIndex);
                    
                    return (
                      <Box key={category} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 0.5, 
                            borderRadius: 1, 
                            bgcolor: alpha(getCategoryColor(category), 0.1),
                            mr: 1,
                            color: getCategoryColor(category)
                          }}>
                            {getCategoryIcon(category)}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: getCategoryColor(category) }}>
                            {category} ({categoryTransactions.length} total)
                          </Typography>
                        </Box>
                        
                        <TableContainer sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                          overflow: 'hidden'
                        }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  bgcolor: 'white',
                                  borderBottom: `2px solid ${getCategoryColor(category)}`,
                                  color: getCategoryColor(category),
                                  py: 1.5,
                                  px: 2.5
                                }}>
                                  Item
                                </TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  bgcolor: 'white',
                                  borderBottom: `2px solid ${getCategoryColor(category)}`,
                                  color: getCategoryColor(category),
                                  py: 1.5,
                                  px: 2.5
                                }}>
                                  Type
                                </TableCell>
                                <TableCell align="right" sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  bgcolor: 'white',
                                  borderBottom: `2px solid ${getCategoryColor(category)}`,
                                  color: getCategoryColor(category),
                                  py: 1.5,
                                  px: 2.5
                                }}>
                                  Quantity
                                </TableCell>
                                <TableCell align="right" sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  bgcolor: 'white',
                                  borderBottom: `2px solid ${getCategoryColor(category)}`,
                                  color: getCategoryColor(category),
                                  py: 1.5,
                                  px: 2.5
                                }}>
                                  Cost
                                </TableCell>
                                <TableCell align="right" sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  bgcolor: 'white',
                                  borderBottom: `2px solid ${getCategoryColor(category)}`,
                                  color: getCategoryColor(category),
                                  py: 1.5,
                                  px: 2.5
                                }}>
                                  Date
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedTransactions.map((transaction, index) => (
                                <TableRow key={index} sx={{
                                  cursor: 'default',
                                  transition: 'all 0.2s ease',
                                  '&:hover': { 
                                    bgcolor: 'grey.50',
                                    transform: 'translateX(4px)',
                                    transition: 'all 0.2s ease',
                                    boxShadow: `inset 4px 0 0 ${getCategoryColor(category)}`
                                  },
                                  '& .MuiTableCell-root': {
                                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                    py: 2,
                                    px: 2.5
                                  }
                                }}>
                                  <TableCell>
                                    <Typography variant="body1" sx={{ 
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      fontSize: '0.95rem'
                                    }}>
                                      {transaction.item}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={transaction.type}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ 
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        borderRadius: 2,
                                        color: getCategoryColor(category),
                                        borderColor: alpha(getCategoryColor(category), 0.3)
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {category === 'Raw Materials' && transaction.unitType && transaction.unitMeasure ? (
                                      <Box>
                                        <Typography variant="body2" sx={{ 
                                          fontWeight: 500,
                                          color: 'text.primary',
                                          fontSize: '0.9rem'
                                        }}>
                                          {formatNumber(transaction.quantity)} {transaction.unitType}
                                        </Typography>
                                        <Typography variant="caption" sx={{ 
                                          color: 'text.secondary',
                                          fontSize: '0.8rem'
                                        }}>
                                          ({formatNumber(transaction.totalQuantity)} {transaction.unitMeasure})
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '0.9rem'
                                      }}>
                                        {formatNumber(transaction.quantity)}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      color: 'success.main',
                                      fontSize: '0.9rem'
                                    }}>
                                      {transaction.cost ? formatCurrency(transaction.cost) : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      fontSize: '0.9rem'
                                    }}>
                                      {formatDate(transaction.date)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {categoryTransactions.length > 10 && (
                          <Box sx={{ 
                            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: `linear-gradient(135deg, ${alpha(getCategoryColor(category), 0.05)}, ${alpha(getCategoryColor(category), 0.02)})`,
                            borderRadius: '0 0 12px 12px'
                          }}>
                            <TablePagination
                              rowsPerPageOptions={[10, 25, 50]}
                              component="div"
                              count={categoryTransactions.length}
                              rowsPerPage={transactionRowsPerPage}
                              page={transactionPage}
                              onPageChange={handleTransactionPageChange}
                              onRowsPerPageChange={handleTransactionRowsPerPageChange}
                              sx={{ 
                                px: 3,
                                py: 2,
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  color: theme.palette.text.primary
                                },
                                '& .MuiTablePagination-select': {
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1,
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  background: 'white',
                                  '&:hover': {
                                    boxShadow: `0 2px 8px ${alpha(getCategoryColor(category), 0.15)}`
                                  }
                                },
                                '& .MuiTablePagination-actions': {
                                  '& .MuiIconButton-root': {
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    borderRadius: 2,
                                    margin: '0 2px',
                                    background: 'white',
                                    color: getCategoryColor(category),
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      background: getCategoryColor(category),
                                      color: 'white',
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 2px 8px ${alpha(getCategoryColor(category), 0.25)}`
                                    },
                                    '&.Mui-disabled': {
                                      background: alpha(theme.palette.grey[300], 0.5),
                                      color: theme.palette.grey[500],
                                      border: `1px solid ${theme.palette.grey[300]}`,
                                      transform: 'none',
                                      boxShadow: 'none'
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No recent transactions</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InventoryData;